import { Link } from "react-router-dom";
import {
  DownloadIcon,
  StarIcon,
  ExternalLinkIcon,
  SparklesIcon,
  TerminalIcon,
  BotIcon,
  ZapIcon,
} from "../ui/Icons";

type FileMetadata = {
  // Common fields
  name?: string;
  description?: string;
  version?: string;
  model?: string;

  // Skill/Command fields
  allowed_tools?: string[];
  argument_hints?: string;

  // Agent fields
  tools?: string[];
  permissionMode?: string;
  skills?: string[];
  color?: string;

  [key: string]: unknown;
};

// Helper to safely render metadata values that might be objects
function safeString(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  // For objects or arrays, return null to prevent React Error #31
  return null;
}

type FileItem = {
  id: string;
  fileType: "SKILL" | "COMMAND" | "AGENT" | "HOOK" | string;
  displayName?: string | null;
  fileName: string;
  description?: string | null;
  metadata?: FileMetadata | null;
};

type PluginCardProps = {
  id: string;
  slug: string;
  name: string;
  description: string;
  author: string;
  tags: string[];
  downloads: number;
  stars: number;
  version?: string;
  featured?: boolean;
  starred?: boolean;
  files?: FileItem[];
  onStar?: (id: string) => void;
  pricingModel?: "FREE" | "ONE_TIME" | "SUBSCRIPTION" | "FREEMIUM";
  price?: number | null;
};

export function PluginCard({
  id,
  slug,
  name,
  description,
  author,
  tags,
  downloads,
  stars,
  featured = false,
  starred = false,
  files,
  onStar,
  pricingModel,
  price,
}: PluginCardProps) {
  const handleStar = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onStar?.(id);
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  // Filter files by type (show all files of each type, not just those with descriptions)
  const skills = files?.filter((f) => f.fileType === "SKILL") || [];
  const commands = files?.filter((f) => f.fileType === "COMMAND") || [];
  const agents = files?.filter((f) => f.fileType === "AGENT") || [];
  const hooks = files?.filter((f) => f.fileType === "HOOK") || [];
  const hasItems =
    skills.length > 0 || commands.length > 0 || agents.length > 0 || hooks.length > 0;

  return (
    <Link
      to={`/plugin/${slug}`}
      className={`card-glow group block p-6 ${featured ? "ring-1 ring-amber/20" : ""}`}
    >
      {/* Header */}
      <div className="mb-4 flex items-start justify-between">
        <div className="flex items-center gap-3">
          {/* Plugin Icon */}
          <div
            className={`flex h-12 w-12 items-center justify-center rounded-xl ${
              featured
                ? "bg-gradient-to-br from-amber/20 to-amber-hover/20 ring-1 ring-amber/30"
                : "bg-elevated/50"
            }`}
          >
            <span className="font-display text-lg font-bold text-amber">
              {name.charAt(0).toUpperCase()}
            </span>
          </div>
          <div>
            <h3 className="font-display text-base font-bold text-primary transition-colors group-hover:text-amber">
              {name}
            </h3>
            <p className="font-mono text-xs text-muted">by {author}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {pricingModel && pricingModel !== "FREE" && price ? (
            <span className="rounded-full bg-success/10 px-2 py-0.5 font-mono text-xs font-medium text-success">
              ${(price / 100).toFixed(2)}
            </span>
          ) : null}
          <button
            onClick={handleStar}
            className={`rounded-lg p-2 transition-colors ${
              starred ? "text-amber" : "text-muted hover:bg-elevated hover:text-secondary"
            }`}
            aria-label={starred ? "Unstar plugin" : "Star plugin"}
          >
            <StarIcon size={18} filled={starred} />
          </button>
        </div>
      </div>

      {/* Description */}
      <p className="mb-4 line-clamp-2 text-sm leading-relaxed text-secondary">{description}</p>

      {/* File Type Items - show max 20 */}
      {hasItems &&
        (() => {
          const MAX_ITEMS = 20;
          const allItems = [
            ...skills.map((s) => ({ ...s, type: "skill" as const })),
            ...commands.map((c) => ({ ...c, type: "command" as const })),
            ...agents.map((a) => ({ ...a, type: "agent" as const })),
            ...hooks.map((h) => ({ ...h, type: "hook" as const })),
          ];
          const displayItems = allItems.slice(0, MAX_ITEMS);
          const remainingCount = allItems.length - MAX_ITEMS;

          // Helper to get display name from metadata or fallback
          const getDisplayName = (item: (typeof displayItems)[0]) => {
            const metaName = safeString(item.metadata?.name);
            if (metaName) return metaName;
            if (item.displayName) return item.displayName;
            const parts = item.fileName.split("/");
            const fileName = parts.pop() || "";
            const baseName = fileName.replace(".md", "").replace(".json", "");
            // For generic filenames like SKILL.md, use parent directory name
            if (["SKILL", "COMMAND", "AGENT", "HOOK"].includes(baseName.toUpperCase())) {
              const parentDir = parts.pop();
              if (parentDir) return parentDir;
            }
            return baseName;
          };

          // Helper to get description from metadata
          const getDescription = (item: (typeof displayItems)[0]) => {
            return safeString(item.metadata?.description) || item.description || null;
          };

          // Helper to build tooltip content
          const getTooltip = (item: (typeof displayItems)[0]) => {
            const parts: string[] = [];
            const desc = getDescription(item);
            if (desc) parts.push(desc);
            const argHints = safeString(item.metadata?.argument_hints);
            if (argHints) {
              parts.push(`Args: ${argHints}`);
            }
            // For skills/commands: allowed_tools
            if (item.metadata?.allowed_tools?.length) {
              const tools = item.metadata.allowed_tools.filter(
                (t): t is string => typeof t === "string"
              );
              if (tools.length) parts.push(`Allowed Tools: ${tools.join(", ")}`);
            }
            // For agents: tools
            if (item.metadata?.tools?.length) {
              const tools = (item.metadata.tools as unknown[]).filter(
                (t): t is string => typeof t === "string"
              );
              if (tools.length) parts.push(`Tools: ${tools.join(", ")}`);
            }
            const permMode = safeString(item.metadata?.permissionMode);
            if (permMode) {
              parts.push(`Permission Mode: ${permMode}`);
            }
            return parts.join("\n\n");
          };

          return (
            <div className="mb-4 flex flex-wrap items-center gap-1.5">
              {displayItems.map((item) => {
                const displayName = getDisplayName(item);
                const tooltip = getTooltip(item);

                if (item.type === "skill") {
                  return (
                    <span
                      key={item.id}
                      className="flex items-center gap-1 rounded-full bg-purple-500/10 px-2 py-0.5 text-xs text-purple-400 ring-1 ring-purple-500/20"
                      title={tooltip || undefined}
                    >
                      <SparklesIcon size={10} />
                      {displayName}
                    </span>
                  );
                }
                if (item.type === "command") {
                  return (
                    <span
                      key={item.id}
                      className="flex items-center gap-1 rounded-full bg-blue-500/10 px-2 py-0.5 text-xs text-blue-400 ring-1 ring-blue-500/20"
                      title={tooltip || undefined}
                    >
                      <TerminalIcon size={10} />/{displayName.toLowerCase().replace(/\s+/g, "-")}
                    </span>
                  );
                }
                if (item.type === "agent") {
                  return (
                    <span
                      key={item.id}
                      className="flex items-center gap-1 rounded-full bg-amber/10 px-2 py-0.5 text-xs text-amber ring-1 ring-amber/20"
                      title={tooltip || undefined}
                    >
                      <BotIcon size={10} />
                      {displayName}
                    </span>
                  );
                }
                if (item.type === "hook") {
                  return (
                    <span
                      key={item.id}
                      className="flex items-center gap-1 rounded-full bg-success/10 px-2 py-0.5 text-xs text-success ring-1 ring-success/20"
                      title={tooltip || undefined}
                    >
                      <ZapIcon size={10} />
                      {displayName}
                    </span>
                  );
                }
                return null;
              })}
              {remainingCount > 0 && (
                <span className="rounded-full bg-elevated/50 px-2 py-0.5 text-xs text-muted">
                  +{remainingCount} more
                </span>
              )}
            </div>
          );
        })()}

      {/* Footer */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {/* Downloads */}
          <div className="flex items-center gap-1.5 text-muted">
            <DownloadIcon size={14} />
            <span className="font-mono text-xs">{formatNumber(downloads)}</span>
          </div>
          {/* Stars */}
          <div className="flex items-center gap-1.5 text-muted">
            <StarIcon size={14} className="text-amber" filled />
            <span className="font-mono text-xs">{formatNumber(stars)}</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-1">
          {tags.slice(0, 2).map((tag) => (
            <span
              key={tag}
              className="rounded-md bg-elevated/50 px-2 py-1 font-mono text-xs text-muted"
            >
              {tag}
            </span>
          ))}
          {tags.length === 0 && (
            <span className="rounded-md bg-elevated/50 px-2 py-1 font-mono text-xs text-muted">
              untagged
            </span>
          )}
        </div>
      </div>

      {/* Hover indicator */}
      <div className="mt-4 flex items-center gap-1 text-sm text-amber opacity-0 transition-opacity group-hover:opacity-100">
        <span>View details</span>
        <ExternalLinkIcon size={14} />
      </div>
    </Link>
  );
}

export function PluginCardSkeleton() {
  return (
    <div className="card-glow animate-pulse p-6">
      <div className="mb-4 flex items-start gap-3">
        <div className="h-12 w-12 rounded-xl bg-elevated/50" />
        <div className="flex-1">
          <div className="mb-2 h-4 w-32 rounded bg-elevated/50" />
          <div className="h-3 w-20 rounded bg-elevated/50" />
        </div>
      </div>
      <div className="mb-2 h-3 w-full rounded bg-elevated/50" />
      <div className="mb-4 h-3 w-2/3 rounded bg-elevated/50" />
      <div className="flex items-center justify-between">
        <div className="flex gap-4">
          <div className="h-4 w-12 rounded bg-elevated/50" />
          <div className="h-4 w-12 rounded bg-elevated/50" />
        </div>
        <div className="h-5 w-16 rounded bg-elevated/50" />
      </div>
    </div>
  );
}
